import { IkeToken, IkeUserRole } from '@ikegps/core-api';
import { CollectionModelFieldTrueSize } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { TopologicalEditorController } from '.';
import { DrawButton } from '../../components/button/draw.button';
import { Carousel } from '../../components/carousel';
import { CenteredMessage } from '../../components/centeredMessage';
import { MaterialIcon } from '../../components/icon/icons';
import { Sidebar } from '../../components/sidebar';
import { addStyles, DefaultTheme } from '../../components/theme';
import { T } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { CapturePreview, CapturePreviewType } from '../../modules/capture.preview/capture.preview';
import { CollectionEdit } from '../../modules/collection.view/collection.view';
import { CSS as DrawToolbarCSS } from '../../modules/draw.toolbar/draw.toolbar.style';
import { MapModeLegend } from '../../modules/map.mode.legend';
import { DistanceMeasurePopup } from '../../modules/distance.measure.popup';
import { LineDrawPopup } from '../../modules/line.draw.popup';
import { MeasurementPopup } from '../../modules/measurement.popup';
import { TopologicalEditorCollectionList } from '../../modules/topological.editor.collection.list';
import { MapPrintController, MapPrintFrame } from '../../modules/topological.editor.print';
import { CSS as TopologicalEditorCollectionListCSS } from '../../modules/topological.editor.collection.list/topological.editor.collection.list.style';
import { TopologicalEditorToolbar } from '../../modules/topological.editor.toolbar';
import type { ITopologicalEditorToolbarOptions } from '../../modules/topological.editor.toolbar/topological.editor.toolbar';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { scrollSelectedIntoListView } from '../../util/scroll-into-list-view';
import { IkePage, type IkePageConfig, IkePageName } from '../page';
import { MapModeInputEvents } from './mm.events';

export const CSS = {
	container: 'c-TopologicalEditor',
	content: 'c-TopologicalEditor__Content',
	contentFraming: 'c-TopologicalEditor__Content--framing',
	divider: 'c-TopologicalEditor__Divider',
	smallContainer: 'c-TopologicalEditor__SmallContainer',
	overlaySave: 'c-TopologicalEditor__OverlaySave',
	framingScrim: 'c-TopologicalEditor__FramingScrim',
	toggleableElement: 'c-ToggleableElement',
};

const Style = {
	[CSS.container]: {
		height: '100%',
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
	},

	[CSS.content]: {
		width: '80%',
		borderLeft: `1px solid ${DefaultTheme.font.color.divider}`,
		position: 'relative',
	},

	[CSS.divider]: {
		width: '100%',
		height: '1px',
		margin: '6px 0',
		background: DefaultTheme.font.color.divider,
	},

	[CSS.smallContainer]: {
		flex: '1',
		overflow: 'hidden',
	},

	[CSS.overlaySave]: {
		position: 'fixed',
		display: 'block',
		width: '100%',
		height: '100%',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		'background-color': 'rgba(0,0,0,0.5)',
		'z-index': 200,
		cursor: 'pointer',
	},
	[CSS.toggleableElement]: {
		minHeight: '0',
		flexGrow: '1',
	},

	// While framing, a full-viewport scrim blocks the side panels (toolbar, collection list, edit form,
	// photomeasure). The map content is raised above it so panning and Show/Hide labels still work.
	[CSS.framingScrim]: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		'background-color': 'rgba(0,0,0,0.35)',
		'z-index': 140,
	},
	[CSS.contentFraming]: {
		'z-index': 145,
	},
};

addStyles(Style);

export const TopologicalEditorPageConfig: IkePageConfig = {
	type: 'internal',
	name: IkePageName.topologicalEditor,
	path: ['/mapmode/:jobId', '/mapmode/:jobId/:collectionUUID'],
	auth: {
		required: true,

		tokens: [IkeToken.FEATURE_MAP_MODE],
		userLevelBlackList: [IkeUserRole.VIEWER, IkeUserRole.DOWNLOADER],
	},
	layout: { nav: false },
};

export class TopologicalEditorPage extends IkePage {
	controller = new TopologicalEditorController();

	streeviewSidebar = new Sidebar();
	collectionSidebar = new Sidebar();
	collectionEdit: CollectionEdit | undefined;

	events: MapModeInputEvents;

	mapPrint = new MapPrintController({
		getDepartmentName: () => Services.Department.current?.displayName ?? '',
		getJobName: () => this.jobName,
		prepareCapture: (showCollectionLabels, showVectorLabels) => {
			const overlay = this.controller.map.overlay;
			if (!overlay) {
				return () => {};
			}
			// Collection and vector labels are set independently from the modal checkboxes at capture time,
			// regardless of the on-map toggle, then restored. Zoom gating is bypassed so the checkboxes
			// stay authoritative at any zoom level.
			const previousCollectionLabels = overlay.viewCollectionLabels;
			const previousVectorLabels = overlay.viewVectorLabels;
			overlay.viewCollectionLabels = showCollectionLabels;
			overlay.viewVectorLabels = showVectorLabels;
			overlay.bypassLabelZoomGating = true;
			// Suppress the hover/selection focus dim so nothing prints faded, even if a feature is
			// selected or gets hovered during the async capture.
			overlay.suppressFocusDim = true;
			return () => {
				overlay.viewCollectionLabels = previousCollectionLabels;
				overlay.viewVectorLabels = previousVectorLabels;
				overlay.bypassLabelZoomGating = false;
				overlay.suppressFocusDim = false;
			};
		},
		// Lock map tools and pole/vector selection while the print frame is being positioned.
		onFramingChange: (active) => this.controller.setPrintFraming(active),
		getLineLegendItems: () => this.controller.visibleLineLegendItems,
	});

	@observable.ref private _carousel: Carousel | null = null;
	@observable private _carouselCollectionRevision: string | null = null;

	constructor() {
		super(TopologicalEditorPageConfig);
		makeObservable(this);

		Services.Window.bindNavigateAway(T.DISCARD_CHANGES_CONFIRM, this.checkExit);

		this.events = new MapModeInputEvents(this);
		this.events.init();

		this.reaction(
			() => this.controller.selectedCollectionRevision,
			(revision) => {
				if (revision != null) {
					scrollSelectedIntoListView(
						TopologicalEditorCollectionListCSS.container,
						TopologicalEditorCollectionListCSS.selected
					);
				}
			}
		);
	}

	@action.bound private checkExit(): boolean {
		// An in-progress (unconfirmed) line blocks navigation like a dirty pole, even though
		// it commits nothing yet — so the user can't silently lose it by navigating away.
		return this.controller.hasChanges || this.controller.lineDrawState.isActive;
	}

	override onRemove() {
		this.controller.destroy();
		Services.Window.unbindNavigateAway(this.checkExit);
		Services.Map.map.getStreetView().setVisible(false);

		this.mapPrint.cancel();

		this.events.destroy();
	}

	get content() {
		return m(
			'div',
			{
				className: `${CSS.content} ${this.controller.contentClassNames} ${
					this.mapPrint.isFraming ? CSS.contentFraming : ''
				}`,
			},
			[
				this.controller.map.view(),
				m('div', { className: DrawToolbarCSS.container }, [
					m('div', { className: DrawToolbarCSS.column }, [m('div', { className: DrawToolbarCSS.group }, this.tools)]),
				]),
				this.controller.measurementState.isActive
					? MeasurementPopup.view({
							onClose: () => this.toggleMeasurement(),
							onPauseChange: () => this.updateMeasurementCursor(),
							measurementState: this.controller.measurementState,
						})
					: null,
				this.controller.distanceMeasureState.isActive
					? DistanceMeasurePopup.view({
							onClose: () => this.toggleDistanceMeasure(),
							distanceMeasureState: this.controller.distanceMeasureState,
						})
					: null,
				this.controller.lineDrawState.isActive
					? LineDrawPopup.view({
							onComplete: () => this.controller.completeLineDraw(),
							onCancel: () => this.controller.cancelLineDraw(),
							lineDrawState: this.controller.lineDrawState,
						})
					: null,
				this.mapPrint.isFraming
					? MapPrintFrame.view({
							region: this.mapPrint.frameRegion,
							pageCount: this.mapPrint.pageCount,
							busy: this.mapPrint.isCapturing,
							onAddPage: () => this.mapPrint.addPage(),
							onExport: () => this.mapPrint.exportFraming(),
							onCancel: () => this.mapPrint.cancelFraming(),
						})
					: null,
			]
		);
	}

	get carousel(): Carousel | null {
		const currentRevision = this.controller.selectedCollectionRevision;

		// Return cached carousel if it's for the same collection
		if (this._carousel && this._carouselCollectionRevision === currentRevision) {
			return this._carousel;
		}

		// Update cache using action for the new collection
		this.updateCarousel(currentRevision);

		return this._carousel;
	}

	@action private updateCarousel(currentRevision: string | null): void {
		// Clear cache if collection changed
		this._carousel = null;
		this._carouselCollectionRevision = currentRevision;

		const selectedCollection = this.controller.selectedCollection;
		if (selectedCollection) {
			let captures: MithrilVNode[] = [];

			selectedCollection.forEachField((field) => {
				if (CollectionModelFieldTrueSize.isTrueSizeField(field)) {
					const captureElements = field.getCaptures().map((capture) =>
						CapturePreview.view(selectedCollection, capture, {
							type: CapturePreviewType.MEDIUM,
							truesize: false,
							icon: true,
							crosshair: false,
						})
					);

					captures = captures.concat(captureElements);
				}
			});

			if (captures.length) {
				this._carousel = new Carousel({
					slides: captures,
					onSlideChange: (index) => this.onSlideChange(index),
				});
			}
		}
	}

	get tools() {
		const isMeasurementActive = this.controller.measurementState.isActive && !this.controller.measurementState.isPaused;
		const isDistanceMeasureActive = this.controller.distanceMeasureState.isActive;
		const isRelocatePoleActive = this.controller.isRelocatePoleActive;
		const isAnyMeasureActive = isMeasurementActive || isDistanceMeasureActive;
		// Drawing a line or span/collection linking locks every other map tool, since each owns
		// map interaction until it's completed or cancelled.
		const locked =
			this.mapPrint.isFraming || this.controller.lineDrawState.isActive || CurrentState.CollectionLink.isLinking;

		return [
			this.controller.getFormHasLocationField()
				? DrawButton.view(MaterialIcon.ADD_LOCATION, {
						flip: false,
						title: T.TOPO_TOOL_CREATE_POLE,
						disabled: isAnyMeasureActive || locked,
						selected: this.controller.isCreatePoleMode,
						solidBackground: true,
						onclick: () => this.controller.toggleCreatePoleMode(),
					})
				: null,
			this.controller.selectedCollection
				? DrawButton.view(MaterialIcon.EDIT_LOCATION, {
						flip: false,
						title: T.TOPO_TOOL_RELOCATE_POLE,
						disabled: isAnyMeasureActive || locked,
						selected: isRelocatePoleActive,
						solidBackground: true,
						onclick: () => this.controller.useTool('relocatePole'),
					})
				: null,
			DrawButton.view(MaterialIcon.VISIBILITY, {
				flip: false,
				title: T.TOPO_SHOW_HIDE_LABELS,
				disabled: false,
				selected: this.controller.map.overlay?.viewCollectionLabels,
				solidBackground: true,
				onclick: () => this.controller.map.overlay?.toggleViewTitles(),
			}),
			DrawButton.view(MaterialIcon.MAGIC_WAND, {
				title: T.TOPO_TOOL_SNAP,
				disabled: isAnyMeasureActive || isRelocatePoleActive || locked,
				selected: false,
				solidBackground: true,
				onclick: () => this.controller.bulkSnap(),
			}),
			DrawButton.view(MaterialIcon.LINEAR_SCALE, {
				title: T.TOPO_TOOL_MEASURE,
				disabled: isRelocatePoleActive || isDistanceMeasureActive || locked,
				selected: this.controller.measurementState.isActive,
				solidBackground: true,
				onclick: () => this.toggleMeasurement(),
			}),
			DrawButton.view(MaterialIcon.HEIGHT, {
				flip: true,
				title: T.TOPO_TOOL_DISTANCE_MEASURE,
				disabled: isRelocatePoleActive || isMeasurementActive || locked,
				selected: isDistanceMeasureActive,
				solidBackground: true,
				onclick: () => this.toggleDistanceMeasure(),
			}),
		];
	}

	@action.bound private toggleMeasurement() {
		const isActive = this.controller.measurementState.isActive;
		this.controller.measurementState.setActive(!isActive);

		if (!isActive) {
			// Deactivate distance measure if active
			this.controller.distanceMeasureState.reset();
			this.controller.lineDrawState.reset();
			// Exit any active tool (e.g., relocate pole) when entering measurement mode
			this.controller.exitTool();
			this.updateMeasurementCursor();
		} else {
			this.controller.getMapModeMap()?.setOptions({ draggableCursor: '' });
			// When closing measurement mode, clear the points
			this.controller.measurementState.clearPoints();
		}
	}

	@action.bound private toggleDistanceMeasure() {
		const isActive = this.controller.distanceMeasureState.isActive;
		this.controller.distanceMeasureState.setActive(!isActive);

		if (!isActive) {
			// Deactivate ruling span if active
			this.controller.measurementState.reset();
			this.controller.lineDrawState.reset();
			// Exit any active tool (e.g., relocate pole)
			this.controller.exitTool();
			this.controller.getMapModeMap()?.setOptions({ draggableCursor: 'crosshair' });
		} else {
			this.controller.getMapModeMap()?.setOptions({ draggableCursor: '' });
		}
	}

	private updateMeasurementCursor() {
		const isPaused = this.controller.measurementState.isPaused;
		this.controller.getMapModeMap()?.setOptions({ draggableCursor: isPaused ? '' : 'crosshair' });
	}

	get editCollectionForm() {
		if (this.controller.selectedCollectionEdit) {
			let carouselOrEmpty = this.carousel;
			let carouselContainer = m(
				'div',
				{
					className: CSS.toggleableElement,
					style: { display: this.controller.selectedCollectionEdit.isCarouselShown ? 'block' : 'none' },
				},
				carouselOrEmpty !== null ? carouselOrEmpty.view() : CenteredMessage.view(T.NO_CAPTURES)
			);
			return m(
				'div',
				{ className: CSS.smallContainer },
				new CollectionEdit(this.controller.selectedCollectionEdit).view(carouselContainer)
			);
		}
		return null;
	}

	@computed get jobName() {
		return this.controller.tag ? this.controller.tag.name : '';
	}

	@action.bound private startMapPrint(): void {
		// Stand down any active measurement / drawing tool so it can't conflict with the capture.
		this.controller.measurementState.reset();
		this.controller.distanceMeasureState.reset();
		// cancelLineDraw (not reset) so the crosshair cursor is cleared too.
		this.controller.cancelLineDraw();
		this.controller.exitTool();
		this.mapPrint.open();
	}

	get titleBar(): ITopologicalEditorToolbarOptions {
		const titleBarParams: ITopologicalEditorToolbarOptions = {
			title: this.jobName,
			printButton: { onClick: () => this.startMapPrint() },
			selectedCollectionData: {
				onClick: (e) => {
					const department = Services.Department.current;
					if (e == null || department == null) {
						return;
					}
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					Services.Window.confirmPageChange(
						e,
						Services.Route.getRouteURL(IkePageName.collectionMeasure, {
							d: department.objectId,
							t: this.controller.tag.objectId,
							...(this.controller.selectedCollection && {
								collection: this.controller.selectedCollection.uuid,
							}),
						})
					);
				},
			},
			saveButton: {
				// Block saving while a line is mid-draw: it isn't a complete edit yet.
				disabled: !(this.controller.hasChanges || this.controller.isSaving) || this.controller.lineDrawState.isActive,
				onClick: () => {
					this.controller.batchSave();
				},
				isSaving: this.controller.isSaving,
			},
		};

		return titleBarParams;
	}

	get nodes() {
		if (Services.Department.current == null) {
			return null;
		}

		if (!Services.Streetview.isReady) {
			return null;
		}

		if (this.controller.routeCollectionUUID) {
			const collection = this.controller.collectionModels.find((c) => c.uuid === this.controller.routeCollectionUUID);
			if (collection && collection.revision !== this.controller.selectedCollectionRevision) {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				Services.Map.isReadyPromise.then(() => {
					this.controller.setSelectedCollectionRevision(collection.revision);
				});
			}
		}

		return [
			m(
				'div',
				{
					className: CSS.container,
				},
				[
					this.collectionSidebar.view(true, [
						TopologicalEditorToolbar.view(this.titleBar),
						TopologicalEditorCollectionList.view({
							onItemClick: (revision: string) => {
								this.controller.setSelectedCollectionRevision(revision);
								this.controller.zoomToSelectedCollection();
							},
							collections: this.controller.collectionModels,
							selectedRevision: this.controller.selectedCollectionRevision,
							collectionsEditController: this.controller,
						}),
						m('div', { className: CSS.divider }),
						MapModeLegend.view(this.controller.map.overlay, this.controller.lineLegendItems),
					]),
					this.content,
					this.controller.selectedCollectionRevision &&
						this.streeviewSidebar.view(false, [
							this.editCollectionForm,
							m('div', { className: CSS.divider }),
							m('div', { className: CSS.smallContainer }, this.controller.streetview.view()),
						]),
				]
			),
			this.mapPrint.isFraming ? m('div', { className: CSS.framingScrim }) : null,
			this.controller.isSaving ? m('div', { className: CSS.overlaySave }) : null,
		];
	}

	override onBeforeUnload() {
		this.controller.collectionEditControllers.forEach((controller) => {
			controller.discardAndDestroyEdit();
		});
	}

	@action.bound private onSlideChange(index: number): void {
		const captureIds = this.controller.getCaptureIds();
		const captureId = captureIds[index];
		if (captureId) {
			this.controller.setSelectedCaptureId(captureId);
		}
	}
}
